// APPROVALS and the APPROVAL REVIEW (UI rebuild batch 4, custody hand-off; Astra's `approvals` and `approval`).
//
// The queue: one register row per card — the payee BY NAME, the purpose, the amount, the expiry, the state — and each
// row opens its review. No signing button on the list (Astra: the transport is chosen on the review). The review: the
// decoded request's facts, the source class, the destination by NAME with no tail (the typing gate), the payout stage,
// the expiry and the approval window, the second device when it is on, the unknown-payee refusal; and the hand-off,
// leading with the phone over Bluetooth (the operator, 22 Oct 2026), the cable only as the quiet other way where the
// browser reaches a Ledger.
//
// KEPT FROM screens/custody/Approvals.tsx: the 3-second read under the same key; listState (a failed poll keeps the
// rows, said above them); cardActions — ONE decision for every entry point (incomplete, expired, unknown payee offer
// nothing, the approval included); useCardApproval held by the page (imported); the signing link minted at intent
// (CosignHandoff, its mint gate); the #card-<txid> deep link from Deposits (now opens that card's review).
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { custody as api, type ApprovalRow } from '../../../lib/api';
import { groupDigits, timeLeft } from '../../../lib/custody-format';
import { fmtDate } from '../../../lib/format';
import { cardActions, cableSigns, listState, whitelistGroup } from '../../../lib/custody-screen';
import { webHid } from '../../../lib/ledger-cable';
import { useCardApproval, type CardApproval } from '../../custody/Approvals';
import { CosignHandoff } from '../../../components/CosignHandoff';
import { Pill } from '../../../components/Pill';
import { PageHead, Panel, Register, SearchBar, SkeletonRows, Notice, EmptyState, StateLine, Fact, ReadFailed, TAP } from '../../../components/v2/kit';
import { CustodyTabs, GatedAddress, kindWord, sourceWord, stageWord, clock, WindowLine } from '../../../components/v2/custody/parts';
import { CableSignV2 } from '../../../components/v2/custody/CableSignV2';
import { ApproveSecond } from '../../../components/v2/custody/ApproveSecond';

const ref = (txid: string) => `#${txid.slice(0, 8)}`;

/** The card's state as one pill word — the same decision every entry point reads (cardActions). */
function CardState({ a }: { a: ApprovalRow }) {
  const left = timeLeft(a.expiresAt);
  const offers = cardActions(a, left.expired);
  if (offers.reason === 'incomplete') return <Pill tone="bad">Incomplete</Pill>;
  if (offers.reason === 'unknown_payee') return <Pill tone="bad">Do not sign</Pill>;
  if (offers.reason === 'expired') return <Pill>Expired</Pill>;
  if (offers.approve) return <Pill tone="warn">Second device</Pill>;
  return <Pill tone="gold">Ready to review</Pill>;
}

/** The payee as the desk names it — or the refusal, never an address. */
function payee(a: ApprovalRow): { text: string; bad: boolean } {
  if (a.partyName) return { text: a.partyName, bad: false };
  if (a.kind === 'payout') return { text: 'Not an approved payout address', bad: true };
  return { text: 'Not named by the desk', bad: false };
}

export function ApprovalsV2() {
  const q = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, refetchInterval: 3_000 });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000, retry: false });
  const [search, setSearch] = useState('');
  const { hash } = useLocation();
  // A link from Deposits names its card (#card-<txid>): the card's own review now.
  if (hash.startsWith('#card-')) return <Navigate to={`/custody/approvals/${hash.slice('#card-'.length)}`} replace />;

  const state = listState(q);
  const rows = q.data?.approvals ?? [];
  const needle = search.trim().toLowerCase();
  const shown = needle ? rows.filter((a) => [a.partyName ?? '', a.amount ?? '', kindWord(a.kind), a.txid].some((s) => s.toLowerCase().includes(needle))) : rows;
  const wallets = balances.data?.wallets;

  return (
    <div className="lg:max-w-[1160px]">
      <PageHead title="Approvals" sub="Named transactions awaiting your review" />
      <CustodyTabs current={null} />
      {state.show === 'failed' ? (
        isCustodySignin(q.error) ? <SigninQuiet error={q.error} /> : <Notice tone="bad" action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t read the approvals.</Notice>
      ) : (
        <>
          {state.stale && <Notice action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t refresh — showing the last read.</Notice>}
          {state.show === 'ready' && rows.length > 0 && <SearchBar value={search} onChange={setSearch} placeholder="Search name, reference or amount" />}
          <Panel flush>
            {state.show === 'pending' ? <SkeletonRows rows={3} what="the approvals" />
              // An empty claim needs a KNOWN read (Screens and Astra, 27 Oct 2026, #6): after a failed refresh, not "nothing".
              : rows.length === 0 ? (q.isError ? <p className="px-4 py-10 text-center text-body text-tg-hint">Not known — the last read failed.</p> : <EmptyState className="py-10">Nothing waiting on you.</EmptyState>)
              : shown.length === 0 ? <EmptyState className="py-10">No approval matches that.</EmptyState>
              : (
                <Register<ApprovalRow>
                  label="Approvals"
                  rows={shown}
                  rowKey={(a) => a.txid}
                  href={(a) => `/custody/approvals/${a.txid}`}
                  columns={[
                    { key: 'payee', head: 'Payee / purpose', width: 'w-[44%]', cell: (a) => {
                      const p = payee(a);
                      return (
                        <span className="block min-w-0">
                          <span className={`block truncate font-medium ${p.bad ? 'text-neg' : ''}`}>{p.text}</span>
                          <span className="mt-1 block text-meta text-tg-hint">{kindWord(a.kind)}{sourceWord(a, wallets) ? ` · from ${sourceWord(a, wallets)}` : ''} · {ref(a.txid)}</span>
                        </span>
                      );
                    } },
                    { key: 'amount', head: 'Amount', align: 'right', cell: (a) => a.amount === null ? <span className="text-neg">Missing</span>
                      : <span className="font-mono tabular">{groupDigits(a.amount)} <span className="text-meta text-tg-hint">USDT</span></span> },
                    { key: 'expires', head: 'Expires', align: 'right', cell: (a) => <span>{clock(a.expiresAt)} <span className="text-meta text-tg-hint">· {timeLeft(a.expiresAt).text}</span></span> },
                    { key: 'state', head: 'Status', align: 'right', width: 'w-[160px]', cell: (a) => <CardState a={a} /> },
                  ]}
                  card={(a) => {
                    const p = payee(a);
                    return {
                      title: <span className={p.bad ? 'text-neg' : ''}>{p.text}</span>,
                      figure: a.amount === null ? <span className="text-neg">Missing</span> : <span className="font-mono tabular">{groupDigits(a.amount)} <span className="text-meta text-tg-hint">USDT</span></span>,
                      meta: `${kindWord(a.kind)} · ${clock(a.expiresAt)} · ${timeLeft(a.expiresAt).text}`,
                      state: <CardState a={a} />,
                    };
                  }}
                />
              )}
          </Panel>
          {state.show === 'ready' && rows.length > 0 && <p className="mt-3 text-meta text-tg-hint">Newest first · Dubai time</p>}
        </>
      )}
    </div>
  );
}

/**
 * The second device's approval, held ABOVE the list and every review (Screens and Astra on the operations screens,
 * 27 Oct 2026, #2): in the review it unmounted with it, so leaving the review lost the busy guard and an unknown
 * submission could be offered again before the list settled it. As v1 holds it above its list (Approvals.tsx).
 */
const ApprovalController = createContext<CardApproval | null>(null);

/** The approvals' host route: the 3-second read and the one approval controller, shared by the list and the reviews. */
export function ApprovalsHostV2() {
  const q = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, refetchInterval: 3_000 });
  const approval = useCardApproval(q.data?.approvals, q.refetch);
  return <ApprovalController.Provider value={approval}><Outlet /></ApprovalController.Provider>;
}

export function ApprovalReviewV2() {
  const { txid = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['custody', 'approvals'], queryFn: api.approvals, refetchInterval: 3_000 });
  const balances = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000, retry: false });
  const whitelist = useQuery({ queryKey: ['custody', 'whitelist'], queryFn: api.whitelist, retry: false });
  // The second device's approval, held by the approvals' host so neither a refresh nor leaving the review unmounts it.
  const approval = useContext(ApprovalController);
  const hasCable = useMemo(() => webHid() !== null, []);
  const mint = useCallback(() => api.cosignLink(txid), [txid]);
  const sheet = params.get('sign');
  const openSheet = (s: 'phone' | 'cable' | null) => setParams(s ? { sign: s } : {}, { replace: true });
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(t); }, []);

  const head = <PageHead title="Approval review" sub={<Link to="/custody" className="btn-link text-meta">← Waiting on you</Link>} />;
  const a = q.data?.approvals.find((x) => x.txid === txid) ?? null;
  // The cable sheet, once opened, is THIS card's for its whole life (#3): mounted on the latest read's eligibility, a
  // refresh that dropped the card — signed, expired or superseded — closed a ceremony in progress and lost its
  // result. Eligibility is checked when it opens; after that the sheet stays until he closes it.
  const cableEligible = !!a && hasCable && cableSigns(a.kind) && cardActions(a, timeLeft(a.expiresAt).expired).sign;
  const [cable, setCable] = useState<string | null>(null);
  useEffect(() => { if (sheet === 'cable' && cable === null && cableEligible && a) setCable(a.txid); }, [sheet, cable, cableEligible, a]);
  const closeCable = () => { setCable(null); openSheet(null); };
  const cableSheet = cable ? <CableSignV2 txid={cable} onClose={closeCable} /> : null;
  // ONE frame for every branch: the page's content as its first child and the cable sheet always its second (Screens
  // and Astra, 27 Oct 2026: drawn at a different place in each branch, the sheet was unmounted and mounted again by a
  // refresh that dropped its card — the ceremony, the typed confirmation and the in-flight guard lost).
  const frame = (content: ReactNode) => <div className="mx-auto max-w-[520px]">{content}{cableSheet}</div>;
  if (!q.data) {
    return frame(<>{head}
      {q.isError ? <ReadFailed what="the approvals" error={q.error} onRetry={() => void q.refetch()} tone="bad" /> : <Panel flush><SkeletonRows rows={4} what="the request" /></Panel>}
    </>);
  }
  if (!a) {
    return frame(<>{head}
      {/* "No longer waiting" only from a current read (#6); after a failed refresh the failure is said instead. */}
      {q.isError ? <ReadFailed what="the approvals" error={q.error} onRetry={() => void q.refetch()} tone="bad" />
        : <Panel><EmptyState action={<Link to="/custody" className="btn-secondary px-4 text-body">Waiting on you</Link>}>No longer waiting on you.</EmptyState></Panel>}
    </>);
  }

  const left = timeLeft(a.expiresAt);
  const offers = cardActions(a, left.expired);
  const p = payee(a);
  // The source by its register name (N-091) when the balances read names the address, else the class word (display only —
  // the signer reads the source from the bytes).
  const fromWallet = balances.data?.wallets.find((w) => w.address === a.fromAddress);
  const from = fromWallet?.name?.full ?? sourceWord(a, balances.data?.wallets);
  // The payout address's stage, read from the address book by its address (display; the server holds the rule).
  const entry = a.kind === 'payout' && a.toAddress ? whitelist.data?.entries.find((e) => e.address === a.toAddress && e.status === 'active') ?? null : null;
  const stage = a.kind !== 'payout' ? null
    : whitelist.isError ? 'Not read'
    : !whitelist.data ? 'Reading…'
    : entry ? (whitelistGroup(entry) === 'unknown' ? 'Not read' : stageWord(entry.stage)) : 'Not in the address book';

  return frame(
    <>
      <PageHead title="Approval review" sub={<>{kindWord(a.kind)} · {ref(a.txid)} · <Link to="/custody" className="btn-link text-meta">Waiting on you</Link></>} />
      {q.isError && <Notice action={<button type="button" className={TAP} onClick={() => void q.refetch()}>Try again</button>}>Couldn&apos;t refresh — showing the last read.</Notice>}
      <section className="overflow-hidden rounded-[10px] border border-stroke bg-panel">
        <div className="border-b border-stroke p-4 lg:p-6">
          <div className="text-label font-medium uppercase tracking-wider text-tg-hint">{kindWord(a.kind)}</div>
          <div className="mt-2 break-words font-mono tabular text-figure font-semibold">
            {a.amount === null ? <span className="text-neg">Amount missing</span> : <>{groupDigits(a.amount)} <span className="text-meta font-normal text-tg-hint">USDT·TRC</span></>}
          </div>
          <div className="mt-2">{left.expired ? <Pill>Expired</Pill> : offers.reason ? <Pill tone="bad">Do not sign</Pill> : <Pill tone="gold">Awaiting your signature</Pill>}</div>
        </div>
        <div className="px-4 lg:px-6">
          <Fact label="From">{from ?? 'Not named by the desk'}</Fact>
          <Fact label="Network">TRC20</Fact>
          <Fact label="Payee"><span className={`line-clamp-2 break-words font-medium ${p.bad ? 'text-neg' : ''}`} title={p.text}>{p.text}</span></Fact>
          <Fact label="Destination">{a.toAddress === null ? <span className="text-neg">Missing</span> : <GatedAddress value={a.toAddress} />}</Fact>
          {stage && <Fact label="Payout stage">{stage}</Fact>}
          {/* The deal the payout settles (P-063), when one was named. */}
          {a.dealId && <Fact label="Deal"><span className="font-mono">{a.dealId}</span></Fact>}
          <Fact label="Expires"><span className="font-mono tabular">{fmtDate(a.expiresAt)}</span> · <span className={left.urgent ? 'text-neg' : 'text-tg-hint'}>{left.text}</span></Fact>
          <Fact label="Signatures"><span className="font-mono tabular">{a.signaturesCollected} / {a.signaturesRequired}</span></Fact>
          {a.approval?.required && (
            <Fact label="Second device">{a.approval.by ? <>by {a.approval.by}{a.approval.at ? <span className="text-tg-hint"> · {fmtDate(a.approval.at)}</span> : null}</> : <span className="text-tg-hint">Awaiting</span>}</Fact>
          )}
        </div>

        <div className="space-y-3 p-4 lg:p-6">
          {/* THE REFUSALS — as today's card: one line each, and then no way through. */}
          {offers.reason === 'incomplete' && (
            <StateLine tone="bad" more="The recipient or the amount is missing from the stored transaction, so nothing here can be checked against the device.">Incomplete card — do not sign; report it.</StateLine>
          )}
          {offers.reason === 'unknown_payee' && (
            <StateLine tone="bad" more="The desk has no payable entry for this destination: it is unregistered, not approved in the Books, disabled, or one of the desk's own wallets that is never paid.">
              Not an approved payout address — do not sign.
            </StateLine>
          )}
          {left.expired && offers.reason === 'expired' && (
            <>
              <StateLine more="A lapsed card is rebuilt from a fresh block when a window is open; extending it would void any signature already collected.">
                Window lapsed — a fresh card comes with the next window.
              </StateLine>
              <WindowLine />
            </>
          )}

          {offers.sign && (
            <>
              <WindowLine />
              {/* The second device's word (N-068), only where it is required — off by default. */}
              {offers.approve && <ApproveSecond a={a} approval={hasCable ? approval : null} />}
              <div className="flex items-start gap-3 rounded-lg border border-stroke bg-panel-2 px-4 py-3">
                <Lock className="mt-0.5 h-4 w-4 shrink-0 text-tg-hint" aria-hidden />
                <StateLine more="The phone opens the signing page; it decodes the signed bytes and asks for the last six characters of the address from your own record. The Ledger's screen is the only display a compromised server cannot change.">
                  Confirm recipient and amount on the Nano.
                </StateLine>
              </div>
              {/* By cable first, every card the cable may sign: his Gen 5 cannot pair with TronLink (N-089, "Yes, cable
                  until the Stax"), so the phone is the second way. A payout follows PAYOUTS_BY_CABLE (cableSigns); with
                  it false again a payout is the phone's alone. No cable in this browser: the phone. */}
              {hasCable && cableSigns(a.kind) ? (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button type="button" className="btn-primary sm:px-6" onClick={() => { setCable(a.txid); openSheet('cable'); }}>Use cable</button>
                    <button type="button" className="btn-secondary px-4 text-body" onClick={() => openSheet('phone')}>Use phone</button>
                  </div>
                  <p className="text-meta text-tg-hint">Signed on the Nano by cable.</p>
                </>
              ) : (
                <>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button type="button" className="btn-primary sm:px-6" onClick={() => openSheet('phone')}>Continue to signing</button>
                  </div>
                  <p className="text-meta text-tg-hint">Signed on your phone over Bluetooth.</p>
                </>
              )}
            </>
          )}
        </div>
      </section>

      <CosignHandoff
        open={sheet === 'phone' && offers.sign}
        onClose={() => openSheet(null)}
        title="Sign on your phone"
        subtitle={`${a.amount ? groupDigits(a.amount) : '—'} USDT → ${a.partyName ?? 'unknown payee'}`}
        mint={mint}
      />
    </>,
  );
}
